import React, { useState } from 'react';

import { Box, Inline } from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import {
  BodyText, Button, HeaderText, TextInput, Stack,
} from '../../../ui';

import { CloseIcon } from '../../../ui/Icons';

/**
 * Reusable multiple choice survey component. Please check out
 * MultipleChoiceSurvey.stories.mdx for example usage.
 */

type TextInputRef = {
  clear: () => any,
};

export type SelectedChoices = {
  [questionNumber: string]: string | number
} & {
  [questionNumber: number]: string | number
} & {
  [textResponse: string]: string
};

type QuestionTypes = {
  question: string;
  choices: string[];
  showInputOnSelection?: (selectedChoices) => boolean,
  inputTitle?: string;
  inputPlaceHolder?: string;
};

type MultipleChoiceSurveyProps = {
  /** Name of survey */
  name: string;
  /** Title of the survey */
  title: string;
  /** Portion of title that is highlighted */
  titleHighlight?: string;
  /** Color of the highlighted portion of the title */
  titleHighlightColor?: string;
  /** The questions and multiple choices for the questions */
  questions: QuestionTypes[];
  /** Allows user to answer more than one question, otherwise user can only answer one question */
  allowMultipleResponse?: boolean;
  /** Question for the input section */
  inputQuestion?: string;
  /** Placeholder text for input section */
  inputPlaceholder?: string;
  /** The submit button text */
  submitBtnText: string;
  /** Function to handle when a choice is selected */
  onChoiceSelected?: (object: SelectedChoices) => void;
  /** Function to handle when the submit button is clicked */
  onSubmit: (object: SelectedChoices) => void;
  /** The background color of the survey */
  backgroundColor?: string;
  /** Boolean to indicate if a response is required for all questions */
  mustAnswerAllQuestions?: boolean;
  /** Renders the close button in upper right corner */
  closeButtonInHeader?: boolean;
  /** The close button text at bottom of survey */
  closeButtonText?: string;
  /** Handles closing survey */
  closeSurvey?: () => void;
};

export const SURVEY_TEXT_RESPONSE = 'textResponse';
export const SURVEY_ON_CHOICE_SELECTED_TEXT_RESPONSE = 'onChoiceSelectedTextResponse';
export const SURVEY_QUESTION_ONE = 1;
export const SURVEY_QUESTION_TWO = 2;
export const SURVEY_QUESTION_THREE = 3;

const MultipleChoiceSurvey = ({
  name,
  title,
  titleHighlight,
  titleHighlightColor = colors.chill,
  questions,
  allowMultipleResponse,
  inputQuestion,
  inputPlaceholder,
  onChoiceSelected,
  submitBtnText,
  onSubmit,
  backgroundColor = colors.chill,
  mustAnswerAllQuestions = false,
  closeButtonInHeader = false,
  closeButtonText,
  closeSurvey,
}: MultipleChoiceSurveyProps) => {
  const [selectedChoices, setSelectedChoices] = useState<SelectedChoices>({});
  const textInputRef = React.createRef<TextInputRef>();

  /**
   * handles selecting the choices from the list of questions on the survey
   * @param questionIndex - the index of the question answered (starts at 1)
   * @param choiceIndex - the index of the choice selected (starts at 1)
   */
  const handleChoiceSelected = (questionIndex, choiceIndex) => {
    let currentSelection = {};
    currentSelection[questionIndex] = choiceIndex;

    // we want to keep the selected choices for the other questions
    if (allowMultipleResponse) {
      currentSelection = { ...selectedChoices, ...currentSelection };
    }

    // we want to clear the custom text input if we do not allow
    // multiple selections and a choice is selected
    if (
      inputQuestion
      && !allowMultipleResponse
      && questionIndex !== SURVEY_TEXT_RESPONSE
    ) {
      textInputRef.current.clear();
    }

    // unselect choice if clicking on an already selected choice
    if (
      questionIndex !== SURVEY_TEXT_RESPONSE
      && questionIndex in selectedChoices
      && selectedChoices[questionIndex] === choiceIndex
    ) {
      delete currentSelection[questionIndex];
    }

    if (onChoiceSelected) {
      onChoiceSelected(currentSelection);
    }

    setSelectedChoices(currentSelection);
  };

  /**
   * handles the onChange event as user types in the input field that is rendered at
   * the very bottom of the survey
   * @param {string} text - the text value entered in the input field
   */
  const handleChangeText = text => {
    let textResponse = { textResponse: text };
    if (allowMultipleResponse) {
      textResponse = { ...selectedChoices, ...textResponse };
    }
    setSelectedChoices(textResponse);
  };

  /**
   * handles the onChange event as the user types in the input field that is rendered
   * as a response to selecting one of the choices for the question(s)
   * @param {string} text - the text value entered in the input field
   */
  const handleChangeTextForOnChoiceSelectedInput = text => {
    let textResponse = { onChoiceSelectedTextResponse: text };
    textResponse = { ...selectedChoices, ...textResponse };
    setSelectedChoices(textResponse);
  };

  /**
   * handle the submit button
   */
  const handleSubmit = () => {
    onSubmit(selectedChoices);
  };

  const btnDisabled = (allowMultipleResponse && mustAnswerAllQuestions)
    ? Object.keys(selectedChoices).length !== questions.length
    : !Object.keys(selectedChoices).length;

  return (
    <Box
      paddingY={32}
      paddingX={24}
      color={backgroundColor}
      overflow="auto"
      style={{
        height: '100%',
      }}
    >
      {closeButtonInHeader && (
        <Box
          style={{
            position: 'absolute',
            right: 12,
            top: 20,
          }}
        >
          <Button
            testID={`${name}-dismiss-btn`}
            appearance="none"
            onClick={closeSurvey}
          >
            <CloseIcon
              width={16}
              height={16}
            />
          </Button>
        </Box>
      )}
      <Box paddingRight={12}>
        <HeaderText
          testID={`${name}-title`}
          level={1}
        >
          {title}
          {!!titleHighlight && (
            <HeaderText
              testID={`${name}-title-higlight`}
              color={titleHighlightColor}
              style={{
                paddingLeft: 8,
              }}
            >
              {titleHighlight}
            </HeaderText>
          )}
        </HeaderText>
      </Box>
      <Box
        testID={`${name}-questions-section`}
        paddingTop={24}
      >
        {questions.map((item, qIndex) => {
          const questionIndex = qIndex + 1;
          return (
            <Box
              testID={`${name}-questionNumber-${questionIndex}`}
              key={`${name}-questionNumber-${questionIndex}`}
            >
              <HeaderText level={3}>
                {item.question}
              </HeaderText>
              <Box
                paddingTop={8}
                paddingBottom={16}
                paddingLeft={8}
              >
                {item.choices.map((choice, cIndex) => {
                  const choiceIndex = cIndex + 1;
                  const selected = (
                    questionIndex in selectedChoices
                    && selectedChoices[questionIndex] === choiceIndex
                  );
                  return (
                    <Button
                      testID={`${name}-question-${questionIndex}-choice-${choiceIndex}-btn`}
                      key={`${name}-question-${questionIndex}-choice-${choiceIndex}`}
                      onClick={() => handleChoiceSelected(questionIndex, choiceIndex)}
                      appearance="none"
                    >
                      <Inline
                        alignItems="center"
                      >
                        <Box
                          testID={`${name}-choice-${selected ? 'selected' : 'notSelected'}-question-${questionIndex}-choice-${choiceIndex}-${selected ? 'selected' : 'not-selected'}`}
                          minHeight={16}
                          minWidth={16}
                          color={selected ? colors.punch : colors.whiteWash}
                          borderRadius={50}
                          borderColor={colors.ash}
                          borderWidth={1}
                          borderStyle="solid"
                          style={{
                            marginTop: '8px',
                            marginBottom: '5px',
                            marginRight: '12px',
                          }}
                        />
                        <BodyText
                          textTransform="none"
                          appearance="normal"
                        >
                          {choice}
                        </BodyText>
                      </Inline>
                    </Button>
                  );
                })}
                {item.showInputOnSelection && item.showInputOnSelection(selectedChoices) && (
                  <Stack
                    paddingTop={16}
                    paddingBottom={8}
                    space={8}
                  >
                    <HeaderText
                      level={3}
                    >
                      {item.inputTitle}
                    </HeaderText>
                    <TextInput
                      testID={`${name}-textInputOnSelection`}
                      ref={textInputRef}
                      accentColor={colors.silver}
                      onChangeText={text => { handleChangeTextForOnChoiceSelectedInput(text); }}
                      placeholder={item.inputPlaceHolder}
                      multiline
                    />
                  </Stack>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      {inputQuestion && (
        <Box
          testID={`${name}-inputQuestion-section`}
          paddingBottom={36}
        >
          <HeaderText
            testID={`${name}-inputQuestion-text`}
            level={3}
          >
            {inputQuestion}
          </HeaderText>
          <Box
            paddingTop={16}
          >
            <TextInput
              testID={`${name}-textInput`}
              ref={textInputRef}
              accentColor={colors.silver}
              onChangeText={text => { handleChangeText(text); }}
              placeholder={inputPlaceholder}
              multiline
            />
          </Box>
        </Box>
      )}
      <Stack
        paddingTop={20}
        paddingLeft={48}
        paddingRight={48}
        space={12}
      >
        <Button
          testID={`${name}-submit-btn-${btnDisabled ? 'disabled' : 'enabled'}`}
          disabled={btnDisabled}
          text={submitBtnText}
          onClick={handleSubmit}
        />

        {closeButtonText && (
          <Button
            testID={`${name}-close-btn`}
            appearance="link-lowercase"
            size="medium"
            text={closeButtonText}
            onClick={closeSurvey}
          />
        )}
      </Stack>
    </Box>
  );
};

export default MultipleChoiceSurvey;
